import type { ReactNode, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
  color?: string;
  strokeWidth?: number;
};

function IconShell({
  size = 24,
  color = 'currentColor',
  strokeWidth = 1.9,
  children,
  ...props
}: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}>
      {children}
    </svg>
  );
}

export function BrandMarkIcon(props: IconProps) {
  return (
    <svg width={24} height={24} viewBox="0 0 1024 1024" fill="none" {...props}>
      <image href="/slotlooks-logo.png" width="1024" height="1024" preserveAspectRatio="xMidYMid meet" />
    </svg>
  );
}

export function DashboardIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M4.5 5.5h6v6h-6z" />
      <path d="M13.5 5.5h6v4h-6z" />
      <path d="M13.5 12.5h6v6h-6z" />
      <path d="M4.5 14.5h6v4h-6z" />
    </IconShell>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M6 5.5h12A1.5 1.5 0 0 1 19.5 7v11A1.5 1.5 0 0 1 18 19.5H6A1.5 1.5 0 0 1 4.5 18V7A1.5 1.5 0 0 1 6 5.5Z" />
      <path d="M4.5 9.5h15" />
      <path d="M8 3.5v4" />
      <path d="M16 3.5v4" />
      <path d="M8 12h.01" />
      <path d="M12 12h.01" />
      <path d="M16 12h.01" />
      <path d="M8 15.5h.01" />
      <path d="M12 15.5h.01" />
      <path d="M16 15.5h.01" />
    </IconShell>
  );
}

export function VisitorIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M6 4.5h12a1.5 1.5 0 0 1 1.5 1.5v12A1.5 1.5 0 0 1 18 19.5H6A1.5 1.5 0 0 1 4.5 18V6A1.5 1.5 0 0 1 6 4.5Z" />
      <path d="M8 9.5h8" />
      <path d="M8 13h8" />
      <path d="M8 16.5h5.5" />
    </IconShell>
  );
}

export function ClientsIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M9 11.2a3.1 3.1 0 1 0 0-6.2 3.1 3.1 0 0 0 0 6.2Z" />
      <path d="M16.8 10.8a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2Z" />
      <path d="M3.8 19.2c1-2.8 3.2-4.2 5.2-4.2s4.2 1.4 5.2 4.2" />
      <path d="M13.6 18.8c.8-1.9 2.4-2.9 4.1-2.9 1.4 0 2.8.6 3.7 1.8" />
    </IconShell>
  );
}

export function AttendanceIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M12 4.5a7.5 7.5 0 1 0 7.5 7.5 7.5 7.5 0 0 0-7.5-7.5Z" />
      <path d="M12 8.2v4.1l2.8 1.7" />
    </IconShell>
  );
}

export function ServicesIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M9 4.5h6a1.5 1.5 0 0 1 1.5 1.5v1h-9v-1A1.5 1.5 0 0 1 9 4.5Z" />
      <path d="M6.5 5.5h11v14h-11z" />
      <path d="M9.2 11h5.6" />
      <path d="M9.2 14h5.6" />
    </IconShell>
  );
}

export function StaffIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M12 12a3.2 3.2 0 1 0 0-6.4A3.2 3.2 0 0 0 12 12Z" />
      <path d="M5.8 19.2c1.2-3 3.7-4.5 6.2-4.5s5 1.5 6.2 4.5" />
    </IconShell>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M10.2 4.5h3.6l.7 2.3a7.2 7.2 0 0 1 1.6.9l2.2-.8 1.8 3.1-1.8 1.5c.1.3.1.7.1 1s0 .7-.1 1l1.8 1.5-1.8 3.1-2.2-.8a7.2 7.2 0 0 1-1.6.9l-.7 2.3h-3.6l-.7-2.3a7.2 7.2 0 0 1-1.6-.9l-2.2.8-1.8-3.1 1.8-1.5a5 5 0 0 1-.1-1c0-.3 0-.7.1-1L2.9 10l1.8-3.1 2.2.8a7.2 7.2 0 0 1 1.6-.9l.7-2.3Z" />
      <path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" />
    </IconShell>
  );
}

export function BellIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M6.5 17.5h11" />
      <path d="M8.5 17.5V11a3.5 3.5 0 1 1 7 0v6.5" />
      <path d="M10.2 17.5a1.8 1.8 0 0 0 3.6 0" />
    </IconShell>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11Z" />
      <path d="m15 15 4 4" />
    </IconShell>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <circle cx="12" cy="12" r="3.5" />
      <path d="M12 4.5v2" />
      <path d="M12 17.5v2" />
      <path d="M4.5 12h2" />
      <path d="M17.5 12h2" />
      <path d="m6.2 6.2 1.4 1.4" />
      <path d="m16.4 16.4 1.4 1.4" />
      <path d="m17.8 6.2-1.4 1.4" />
      <path d="m7.6 16.4-1.4 1.4" />
    </IconShell>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M15.5 4.6a7.5 7.5 0 1 0 3.9 12.8 8.1 8.1 0 0 1-3.9-12.8Z" />
    </IconShell>
  );
}

export function MonitorIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <rect x="4.5" y="5.5" width="15" height="11" rx="2" />
      <path d="M9 19.5h6" />
      <path d="M12 16.5v3" />
    </IconShell>
  );
}

export function LogoutIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M10 5.8H7.2A2.7 2.7 0 0 0 4.5 8.5v7a2.7 2.7 0 0 0 2.7 2.7H10" />
      <path d="M13.2 8.5 17.5 12l-4.3 3.5" />
      <path d="M17.5 12H9.2" />
    </IconShell>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M3.5 12s3.2-5.2 8.5-5.2 8.5 5.2 8.5 5.2-3.2 5.2-8.5 5.2S3.5 12 3.5 12Z" />
      <path d="M12 14.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2Z" />
    </IconShell>
  );
}

export function EyeOffIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="m4.5 4.5 15 15" />
      <path d="M9.5 7.2A8.9 8.9 0 0 1 12 6.8c5.3 0 8.5 5.2 8.5 5.2a15 15 0 0 1-2.2 2.8" />
      <path d="M14.6 16.8a8.8 8.8 0 0 1-2.6.4C6.7 17.2 3.5 12 3.5 12a15 15 0 0 1 2.4-3" />
      <path d="M10.2 10.2a2.6 2.6 0 0 0 3.6 3.6" />
    </IconShell>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M14.5 6.5 9 12l5.5 5.5" />
    </IconShell>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="m9.5 6.5 5.5 5.5-5.5 5.5" />
    </IconShell>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M12 5.5v13" />
      <path d="M5.5 12h13" />
    </IconShell>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M6.5 6.5 17.5 17.5" />
      <path d="M17.5 6.5 6.5 17.5" />
    </IconShell>
  );
}

export function TrendIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M4.5 18.5h15" />
      <path d="M6 15.5 10.2 11l3 2.7 4.8-6.2" />
      <path d="M16.5 7.5h1.5V9" />
    </IconShell>
  );
}

export function WalletIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M5.5 7.5h13A1.5 1.5 0 0 1 20 9v6a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 15V9a1.5 1.5 0 0 1 1.5-1.5Z" />
      <path d="M17.5 11h2" />
      <path d="M6.5 7.5V6a1.5 1.5 0 0 1 1.5-1.5h9" />
    </IconShell>
  );
}

export function MapPinIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M12 21s5.5-4.8 5.5-10.3a5.5 5.5 0 1 0-11 0C6.5 16.2 12 21 12 21Z" />
      <path d="M12 13.1a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z" />
    </IconShell>
  );
}

export function SparkIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.8L12 18l-1.7-5.5L4.8 10.7 10.3 9 12 3.5Z" />
    </IconShell>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <IconShell {...props}>
      <polyline points="20 6 9 17 4 12" />
    </IconShell>
  );
}
